import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  Segment,
  Button,
  Menu,
  Table,
  Dimmer,
  Loader,
  Grid,
  Statistic,
  Icon,
  Progress,
} from 'semantic-ui-react';
import { getMeetingStatistics } from '../api/MeetingStatisticsApi';
import { MeetingStatisticsDTO, ProblemMeetingDTO } from '../dto/MeetingStatisticsDTOs';
import { sendWarningMessageToReportedBookcommunity } from '../api/BookcommunityManagementApi';

const MeetingStatisticsPage: React.FC = () => {
  // TODO: 실제 인증 토큰을 localStorage나 context에서 가져오도록 수정
  const authToken = localStorage.getItem('accessToken') || '';

  // 날짜 포맷팅 함수 (YYYY-MM-DD)
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 내일 날짜 계산
  const getTomorrow = (): string => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return formatDate(tomorrow);
  };

  // N일 전 날짜 계산
  const getDaysAgo = (days: number): string => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return formatDate(date);
  };

  // 오늘 날짜 계산
  const getToday = (): string => {
    return formatDate(new Date());
  };

  // 초기 날짜 계산
  const getInitialStartDate = (): string => {
    return getDaysAgo(7);
  };

  const getInitialEndDate = (): string => {
    return getTomorrow();
  };

  const [loading, setLoading] = useState(true);
  const [statisticsData, setStatisticsData] = useState<MeetingStatisticsDTO | null>(null);
  const [startDate, setStartDate] = useState(getInitialStartDate());
  const [endDate, setEndDate] = useState(getInitialEndDate());
  const [selectedPeriod, setSelectedPeriod] = useState('7days');
  const [dateInputEnabled, setDateInputEnabled] = useState(false);

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
    
    if (period === 'today') {
      setDateInputEnabled(false);
      setStartDate(getToday());
      setEndDate(getTomorrow());
    } else if (period === '7days') {
      setDateInputEnabled(false);
      setStartDate(getDaysAgo(7));
      setEndDate(getTomorrow());
    } else if (period === '30days') {
      setDateInputEnabled(false);
      setStartDate(getDaysAgo(30));
      setEndDate(getTomorrow());
    } else if (period === 'dateInput') {
      setDateInputEnabled(true);
    }
  };

  const loadStatisticsData = async () => {
    setLoading(true);
    try {
      const result = await getMeetingStatistics(authToken, startDate, endDate);
      if (result && result.errorCode === 0) {
        setStatisticsData(result.response);
      }
    } catch (error) {
      console.error('모임 통계 데이터 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatisticsData();
  }, [startDate, endDate]);

  const handleSearch = () => {
    loadStatisticsData();
  };

  const handleSendWarningToBookcommunity = async (item: ProblemMeetingDTO) => {
    try {
      if (!item.bookcommunityId) {
        alert('북커뮤니티 ID를 찾을 수 없습니다.');
        return;
      }
      const defaultMessage = '운영정책 위반 관련 경고 메시지입니다.';
      await sendWarningMessageToReportedBookcommunity(authToken, item.bookcommunityId, defaultMessage);
      alert('경고 메시지를 발송했습니다.');
    } catch (e) {
      console.error('경고 발송 실패:', e);
      alert('경고 발송에 실패했습니다.');
    }
  };

  if (loading) {
    return (
      <Container fluid>
        <Dimmer active inverted>
          <Loader size="large">데이터를 불러오는 중...</Loader>
        </Dimmer>
      </Container>
    );
  }

  if (!statisticsData) {
    return (
      <Container fluid>
        <Header as="h2">데이터를 불러올 수 없습니다.</Header>
      </Container>
    );
  }

  return (
    <Container fluid>
      {/* 상단 제목 */}
      <Header as="h2" style={{ marginBottom: 16 }}>
        3. 모임 통계
      </Header>

      {/* 기간 선택 + 상단 탭 */}
      <Segment
        raised
        style={{
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#f8f9ff',
          marginBottom: 24,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ marginRight: 8, fontWeight: 500 }}>시작일</span>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={!dateInputEnabled}
            style={{
              padding: '6px 10px',
              borderRadius: 4,
              border: '1px solid #d0d4e4',
              backgroundColor: dateInputEnabled ? '#fff' : '#f5f5f5',
              cursor: dateInputEnabled ? 'text' : 'not-allowed',
            }}
          />
          <span style={{ margin: '0 4px' }}>~</span>
          <span style={{ marginRight: 8, fontWeight: 500 }}>종료일</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={!dateInputEnabled}
            style={{
              padding: '6px 10px',
              borderRadius: 4,
              border: '1px solid #d0d4e4',
              backgroundColor: dateInputEnabled ? '#fff' : '#f5f5f5',
              cursor: dateInputEnabled ? 'text' : 'not-allowed',
            }}
          />
          <Button primary style={{ marginLeft: 12 }} onClick={handleSearch}>
            검색
          </Button>
        </div>

        <Menu compact>
          <Menu.Item
            active={selectedPeriod === 'today'}
            onClick={() => handlePeriodChange('today')}
          >
            오늘
          </Menu.Item>
          <Menu.Item
            active={selectedPeriod === '7days'}
            onClick={() => handlePeriodChange('7days')}
          >
            7일
          </Menu.Item>
          <Menu.Item
            active={selectedPeriod === '30days'}
            onClick={() => handlePeriodChange('30days')}
          >
            30일
          </Menu.Item>
          <Menu.Item
            active={selectedPeriod === 'dateInput'}
            onClick={() => handlePeriodChange('dateInput')}
          >
            날짜입력
          </Menu.Item>
        </Menu>
      </Segment>

      {/* 상단 카드 영역 */}
      <Grid columns={2} stackable style={{ marginBottom: 24 }}>
        <Grid.Row>
          {/* 활성 모임 수 */}
          <Grid.Column>
            <Segment raised>
              <Header as="h4" style={{ marginBottom: 16 }}>
                활성 모임 수
              </Header>
              <div style={{ display: 'flex', gap: 16 }}>
                <div
                  style={{
                    flex: 1,
                    background: '#1b4ed0',
                    color: '#fff',
                    padding: '20px',
                    borderRadius: 8,
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: 32, fontWeight: 700, marginBottom: 8 }}>
                    {statisticsData.activeMeetingSummary.total}
                  </div>
                  <div style={{ fontSize: 14, opacity: 0.9 }}>전체</div>
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>
                    진행중: {statisticsData.activeMeetingSummary.inProgress}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>
                    종료임박: {statisticsData.activeMeetingSummary.nearingCompletion}
                  </div>
                </div>
              </div>
            </Segment>
          </Grid.Column>

          {/* 신규 모임 */}
          <Grid.Column>
            <Segment raised>
              <Header as="h4" style={{ marginBottom: 16 }}>
                신규 모임
              </Header>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Statistic>
                  <Statistic.Value>
                    {statisticsData.newMeeting.count}
                    {statisticsData.newMeeting.trend === 'up' && (
                      <Icon name="arrow up" color="green" style={{ marginLeft: 8 }} />
                    )}
                    {statisticsData.newMeeting.trend === 'down' && (
                      <Icon name="arrow down" color="red" style={{ marginLeft: 8 }} />
                    )}
                  </Statistic.Value>
                </Statistic>
              </div>
            </Segment>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {/* 하단 테이블 영역 */}
      <div style={{ display: 'flex', gap: 24, alignItems: 'stretch' }}>
        {/* 왼쪽: 모임 참여율 상위 모임 TOP 10 */}
        <Segment raised style={{ flex: 1, padding: '20px', margin: 0 }}>
          <Header
            as="h4"
            style={{
              marginBottom: 16,
              marginTop: 0,
              minHeight: '28px',
              height: '28px',
              lineHeight: '28px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            모임 참여율 상위 모임 TOP 10
          </Header>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {statisticsData.topMeetingsByParticipation.map((meeting) => (
              <div key={meeting.rank} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>
                    {meeting.rank}. {meeting.meetingName}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#1b4ed0' }}>
                    책 {meeting.bookCount ?? 0}권 · 게시글 {meeting.textCount ?? 0}개
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Segment>

        {/* 오른쪽: 문제 모임 경고 목록 */}
        <Segment raised style={{ flex: 1, padding: '20px', margin: 0 }}>
          <Header
            as="h4"
            style={{
              marginBottom: 16,
              marginTop: 0,
              minHeight: '28px',
              height: '28px',
              lineHeight: '28px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            문제 모임 경고 목록 (상위 10개)
          </Header>
          <Table celled style={{ marginTop: 0 }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>모임명</Table.HeaderCell>
                <Table.HeaderCell>신고 건수</Table.HeaderCell>
                <Table.HeaderCell>관리</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {statisticsData.problemMeetings.map((meeting, index) => (
                <Table.Row key={index}>
                  <Table.Cell>{meeting.meetingName}</Table.Cell>
                  <Table.Cell>{meeting.reportCount}</Table.Cell>
                  <Table.Cell>
                    <Button size="small" color="orange" onClick={() => handleSendWarningToBookcommunity(meeting)}>
                      경고 발송
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Segment>
      </div>
    </Container>
  );
};

export default MeetingStatisticsPage;
